import * as React from "react";
import { IsDefined } from "../../Core/Comparison";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useSecurityContext } from "../../Core/SecurityContext";
import { PermissionEnum } from "../api/ApiPermissionEnum";
import { Permission } from "../api/apiTypes";

interface AuthorizationProps {
    currentPermissions: Array<Permission>;
    studyId: string;
    permissionId?: string;
    permissionIdsAny?: string[];
    permissionIdsAll?: string[];
}

interface GlobalAuthorizationProps {
    currentPermissions: Array<Permission>;
    permissionId?: string;
    permissionIdsAny?: string[];
}

const HasElements = (input: any[]): boolean => IsDefined(input) && input.length > 0;

export const HasPermission = (
    currentPermissions: Permission[],
    studyId: string,
    permissionId: string
): boolean => {
    return (
        currentPermissions &&
        Array.isArray(currentPermissions) &&
        currentPermissions.length > 0 &&
        currentPermissions.some(
            permission =>
                (permission.StudyId === studyId || !permission.StudyId) &&
                permission.Id === permissionId
        )
    );
}

export const HasPermissionMultiStudies = (
    currentPermissions: Permission[],
    studyIds: string[],
    permissionId: string
): boolean => {
    return (
        currentPermissions &&
        Array.isArray(currentPermissions) &&
        currentPermissions.length > 0 &&
        currentPermissions.some(
            permission =>
                (studyIds.includes(permission.StudyId) || !permission.StudyId) &&
                permission.Id === permissionId
        )
    );
}

export const HasGlobalPermission = (
    currentPermissions: Permission[],
    permissionId: string
): boolean => {
    return (
        currentPermissions &&
        Array.isArray(currentPermissions) &&
        currentPermissions.length > 0 &&
        currentPermissions.some(
            permission =>
                permission.Id === permissionId
        )
    );
}

export const Authorization = ({
    currentPermissions,
    studyId,
    permissionId,
    permissionIdsAny,
    permissionIdsAll,
    children
}: React.PropsWithChildren<AuthorizationProps>): JSX.Element => {
    let authorizationGranted = false;

    if (permissionIdsAll)
        authorizationGranted = HasAllPermissions(permissionIdsAll, currentPermissions, studyId);
    else if (permissionIdsAny)
        authorizationGranted = HasAnyPermissions(permissionIdsAny, currentPermissions, studyId);
    else
        authorizationGranted = HasPermission(currentPermissions, studyId, permissionId);

    if (!ValidateAuthorizationParameters({currentPermissions, studyId, permissionId, permissionIdsAny, permissionIdsAll})) {
        throw new Error("Authorization requires at least one permission to be specified.");
    }

    return authorizationGranted ? <>{children}</> : null;
}

const HasAllPermissions = (requiredPermissions: any[], currentPermissions: Permission[], studyId: string): boolean =>
    requiredPermissions.every(permissionId => HasPermission(currentPermissions, studyId, permissionId));

const HasAnyPermissions = (requiredPermissions: any[], currentPermissions: Permission[], studyId: string): boolean =>
    requiredPermissions.some(permissionId => HasPermission(currentPermissions, studyId, permissionId));

const ValidateAuthorizationParameters = (props: AuthorizationProps): boolean => {
    return (
            IsDefined(props.permissionId)
        ||  HasElements(props.permissionIdsAll)
        ||  HasElements(props.permissionIdsAny))
}

export const GlobalAuthorization = ({
    currentPermissions,
    permissionId,
    permissionIdsAny,
    children
}: React.PropsWithChildren<GlobalAuthorizationProps>): JSX.Element => {

    const needsToHaveAllPermissions = permissionIdsAny ? permissionIdsAny : [permissionId];

    const hasAllPermissions = needsToHaveAllPermissions.some(permissionId =>
        HasGlobalPermission(currentPermissions, permissionId)
    );

    if (!permissionId && (!permissionIdsAny || permissionIdsAny.length < 1)) {
        throw new Error("Authorization requires at least one permission to be specified.");
    }

    return hasAllPermissions ? <>{children}</> : null;
}

export const useHasPermission = (permission: PermissionEnum) => {
    const { currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();

    return HasPermission(currentUserPermissions, study.id, permission);
}

export const useHasSomePermissions = (permissions: PermissionEnum[]) => {
    const { currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();

    return permissions.some(permissionId =>
        HasPermission(currentUserPermissions, study.id, permissionId)
    );
}

export const useHasSomeGlobalPermissions = (permissions: PermissionEnum[]) => {
    const { currentUserPermissions } = useSecurityContext();

    return permissions.some(permissionId =>
        HasGlobalPermission(currentUserPermissions, permissionId)
    );
}
